/* ─────────────────────────────────────────────────────────────────────────────
   GrowthOS — Decision / Insights. A "what needs a decision" dashboard, laid out
   like the original app: three labelled groups (Customers & Performance,
   Marketing, HubSpot), each holding colour-bifurcated sections that are
   collapsed by default with the chevron on the left. Every table is the
   original-style DataTable (S.No, sticky header, sort, filter, pagination) and
   every number that has rows behind it opens them: closed-deal groups, TAT
   buckets, POC brands / closed deals, the Marketing / Email / LinkedIn strip
   cards (values lifted from those dashboards), HubSpot gap tiles and POC gap
   cells, and the Industry → sub-category → brand tree.
   ──────────────────────────────────────────────────────────────────────────── */
import React, { useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { Box, MenuItem, Paper, Select, TextField, Typography } from '@mui/material'
import { Bar, BarChart, CartesianGrid, Legend, ResponsiveContainer, Tooltip as RTooltip, XAxis, YAxis } from 'recharts'
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents'
import TimerIcon from '@mui/icons-material/Timer'
import TrendingDownIcon from '@mui/icons-material/TrendingDown'
import PersonOffIcon from '@mui/icons-material/PersonOff'
import RepeatIcon from '@mui/icons-material/Repeat'
import GroupsIcon from '@mui/icons-material/Groups'
import ViewInArIcon from '@mui/icons-material/ViewInAr'
import FactCheckIcon from '@mui/icons-material/FactCheck'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import LightbulbIcon from '@mui/icons-material/Lightbulb'
import LayersIcon from '@mui/icons-material/Layers'
import CampaignIcon from '@mui/icons-material/Campaign'
import MailOutlineIcon from '@mui/icons-material/MailOutline'
import LinkedInIcon from '@mui/icons-material/LinkedIn'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import CalendarTodayIcon from '@mui/icons-material/CalendarToday'
import { useGetDecisionInsightsQuery } from '../../redux/growthosDecisionInsightsApiSlice'
import { useGetHubspotCompanyGapQuery } from '../../redux/growthosHubspotReviewApiSlice'
import { useListIngestRunsQuery } from '../../redux/growthosConnectorsApiSlice'
import {
  GX_BORDER, GX_COLORS, GX_INK_500, GX_INK_700, GX_INK_900, GX_NEGATIVE, GX_PAGE_SX, GX_POSITIVE, GxPill, GxSyncStatusBar,
} from '../growthos-shared/GrowthOsUi'
import {
  Badge, Clean, CountButton, DataTable, ExpandableTable, GX_INK_400, GrowthPct, HsLink,
  currency, daysAgoLabel, hsRecordUrl, integer, shortDate,
} from '../growthos-hubspot-review/HsKit'
import { GxPagedTable } from '../growthos-shared/GxPagedTable'

const PERIODS = [
  { value: 'mtd', label: 'MTD' }, { value: 'last_month', label: 'Last Month' },
  { value: 'last_3m', label: 'Last 3M' }, { value: 'last_6m', label: 'Last 6M' },
  { value: 'ytd', label: 'YTD' }, { value: 'all', label: 'All time' }, { value: 'custom', label: 'Custom' },
]
const SYNC_LABELS = { proship_api: 'ProShip', hubspot: 'HubSpot', hubspot_api: 'HubSpot' }
const pocHref = (personId) => (personId ? `/growthos/pocs-analytics/${personId}` : undefined)
const linkSx = { color: '#4338ca', fontWeight: 600, textDecoration: 'none' }

function PocName({ personId, name, bold = true }) {
  const href = pocHref(personId)
  if (!href) return bold ? <b>{name ?? '—'}</b> : (name ?? '—')
  return <Link to={href} onClick={(e) => e.stopPropagation()} style={linkSx}>{name}</Link>
}

// ── Section chrome ───────────────────────────────────────────────────────────
function SourcePills({ sources }) {
  return (
    <Box component="span" sx={{ ml: 1, display: 'inline-flex', gap: 0.5, verticalAlign: 'middle' }}>
      {sources.map((s) => (
        <Box component="span" key={s} sx={{
          px: 1, py: 0.25, borderRadius: '9999px', fontSize: 10, fontWeight: 600,
          ...(s === 'HubSpot' ? { bgcolor: 'rgba(79,70,229,0.1)', color: '#4f46e5' } : { bgcolor: 'rgba(5,150,105,0.1)', color: '#059669' }),
        }}>{s}</Box>
      ))}
    </Box>
  )
}

// Amber = holistic (All Pipelines), slate = a single scoped pipeline.
function ScopePill({ label }) {
  const all = /all pipeline/i.test(label)
  return (
    <Box component="span" sx={{
      ml: 0.75, px: 1, py: 0.25, borderRadius: '9999px', fontSize: 10, fontWeight: 600, verticalAlign: 'middle',
      ...(all ? { bgcolor: 'rgba(217,119,6,0.12)', color: '#b45309' } : { bgcolor: 'rgba(100,116,139,0.14)', color: '#475569' }),
    }}>{label}</Box>
  )
}

/** A labelled band grouping related Sections — a heading, not another toggle. */
function SectionGroup({ title, subtitle, icon, color, children }) {
  return (
    <Box sx={{ mb: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, px: 0.5, pt: 1, mb: 1.5 }}>
        <Box sx={{ width: 28, height: 28, flexShrink: 0, borderRadius: '8px', bgcolor: color, color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{icon}</Box>
        <Box>
          <Typography sx={{ fontSize: 13, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: GX_INK_700 }}>{title}</Typography>
          {subtitle && <Typography sx={{ fontSize: 12, color: GX_INK_400 }}>{subtitle}</Typography>}
        </Box>
        <Box sx={{ ml: 1, height: '1px', flex: 1, bgcolor: GX_BORDER }} />
      </Box>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>{children}</Box>
    </Box>
  )
}

const SHELL_SX = {
  position: 'relative', overflow: 'hidden', borderRadius: '16px', border: '1px solid rgba(148,163,184,0.26)',
  background: 'linear-gradient(165deg, #ffffff 0%, #fcfdff 60%, #f5f7fd 100%)',
  boxShadow: '0 1px 0 rgba(255,255,255,0.9) inset, 0 1px 2px rgba(15,23,42,0.05), 0 10px 24px -14px rgba(15,23,42,0.22)',
}

/** Collapsed-by-default section: accent bar, chevron on the LEFT, coloured icon, title (count) + pills. */
function Section({ title, subtitle, icon, color, count, sources, scopeLabel, children }) {
  const [open, setOpen] = useState(false)
  return (
    <Box sx={SHELL_SX}>
      <Box sx={{ position: 'absolute', left: 0, right: 0, top: 0, height: 4, bgcolor: color }} />
      <Box component="button" onClick={() => setOpen((o) => !o)} aria-expanded={open}
        sx={{ width: '100%', display: 'flex', alignItems: 'center', gap: 1.5, px: 2.5, pt: 2.5, pb: 1.5, border: 'none', bgcolor: 'transparent', cursor: 'pointer', textAlign: 'left', fontFamily: 'inherit' }}>
        <ChevronRightIcon sx={{ fontSize: 20, color, transition: 'transform .15s', transform: open ? 'rotate(90deg)' : 'none' }} />
        <Box sx={{ width: 36, height: 36, flexShrink: 0, borderRadius: '12px', bgcolor: color, color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{icon}</Box>
        <Box sx={{ flex: 1 }}>
          <Typography component="div" sx={{ fontSize: 15, fontWeight: 600, color: GX_INK_900 }}>
            {title}
            {count != null && <Box component="span" sx={{ ml: 1, fontSize: 12, fontWeight: 400, color: GX_INK_400 }}>({integer(count)})</Box>}
            {scopeLabel && <ScopePill label={scopeLabel} />}
            <SourcePills sources={sources} />
          </Typography>
          {subtitle && <Typography sx={{ fontSize: 12, color: GX_INK_400 }}>{subtitle}</Typography>}
        </Box>
      </Box>
      {open && <Box sx={{ borderTop: `1px solid ${GX_BORDER}`, px: 2.5, py: 2 }}>{children}</Box>}
    </Box>
  )
}

const Surface = ({ children, sx }) => <Box sx={{ bgcolor: '#f8fafc', p: 1.5, ...sx }}>{children}</Box>
const DrillLabel = ({ children }) => <Typography sx={{ mb: 0.5, fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', color: GX_INK_400 }}>{children}</Typography>
const LiveBadge = ({ live }) => (live ? <Badge color="#065f46" bg="#d1fae5">Live</Badge> : <Badge color="#be123c" bg="#ffe4e6">Not live</Badge>)
const lastShipCell = (last, days) => <>{last ? shortDate(last) : '—'} <Box component="span" sx={{ fontSize: 11, color: GX_INK_400 }}>({daysAgoLabel(days)})</Box></>

// ── Shared deal / brand tables ─────────────────────────────────────────────
function ClosedDealTable({ deals, hub }) {
  return (
    <DataTable
      rows={deals || []}
      rowKey={(d) => d.dealId}
      emptyText="None."
      columns={[
        { key: 'deal', header: 'Deal', title: 'HubSpot deal name.', sortValue: (d) => d.dealName ?? '', cell: (d) => <b>{d.dealName}</b> },
        { key: 'brand', header: 'Brand', title: 'Brand name (ProShip / Data → Brands).', sortValue: (d) => d.brandName ?? '', cell: (d) => d.brandName ?? '—' },
        { key: 'poc', header: 'Sales POC', title: 'Deal owner (Sales POC) on HubSpot.', sortValue: (d) => d.poc ?? '', cell: (d) => <PocName personId={d.personId} name={d.poc} bold={false} /> },
        { key: 'pipeline', header: 'Pipeline', title: 'HubSpot pipeline.', sortValue: (d) => d.pipeline ?? '', cell: (d) => d.pipeline ?? '—' },
        { key: 'source', header: 'Deal Source', title: 'Deal Source sub-category (marketing channel).', sortValue: (d) => d.subcategory ?? '', cell: (d) => d.subcategory ?? '—' },
        { key: 'closed', header: 'Closed', title: 'Deal close date (HubSpot).', sortValue: (d) => d.closedDate ?? '', cell: (d) => shortDate(d.closedDate) },
        { key: 'firstship', header: '1st Shipment', title: "The brand's first-ever positive ProShip shipment date (blank until the brand goes live).", sortValue: (d) => d.firstShipmentDate ?? '', cell: (d) => shortDate(d.firstShipmentDate) },
        { key: 'revpot', header: 'Rev. potential', title: 'Estimated MBR (₹/month) from the deal.', align: 'right', sortValue: (d) => d.revenuePotential, cell: (d) => currency(d.revenuePotential) },
        { key: 'shippot', header: 'Ship. potential', title: 'Expected monthly shipments from the deal.', align: 'right', sortValue: (d) => d.shipmentPotential, cell: (d) => integer(d.shipmentPotential) },
        { key: 'actrev', header: 'Actual rev.', title: 'Lifetime ProShip revenue since the brand went live.', align: 'right', sortValue: (d) => d.actualRevenue, cell: (d) => currency(d.actualRevenue) },
        { key: 'actship', header: 'Actual ship.', title: 'Lifetime ProShip shipments since the brand went live.', align: 'right', sortValue: (d) => d.actualShipments, cell: (d) => integer(d.actualShipments) },
        { key: 'status', header: 'Status', title: 'Live once the brand has shipped at least once.', sortValue: (d) => (d.live ? 1 : 0), cell: (d) => <LiveBadge live={d.live} /> },
        { key: 'hubspot', header: 'HubSpot', title: 'Opens the record in HubSpot.', cell: (d) => <HsLink href={hsRecordUrl(hub, '0-3', d.dealId)} label="Deal" /> },
      ]}
    />
  )
}

function ActiveBrandsTable({ brands }) {
  return (
    <DataTable
      rows={brands || []}
      rowKey={(b) => b.brandId}
      emptyText="None."
      columns={[
        { key: 'brand', header: 'Brand', title: 'Brand name (ProShip / Data → Brands).', sortValue: (b) => b.brandName, cell: (b) => <b>{b.brandName}</b> },
        { key: 'ship', header: 'Shipments', title: 'ProShip shipments in the selected window.', align: 'right', sortValue: (b) => b.shipments, cell: (b) => integer(b.shipments) },
        { key: 'rev', header: 'Revenue', title: 'ProShip revenue (₹) in the selected window.', align: 'right', sortValue: (b) => b.revenue, cell: (b) => currency(b.revenue) },
        { key: 'lastShip', header: 'Last shipment', title: "The brand's most recent positive shipment, measured against the ProShip data-through date.", sortValue: (b) => b.daysSinceShipment ?? -1, cell: (b) => lastShipCell(b.lastShipment, b.daysSinceShipment) },
      ]}
    />
  )
}

// Declining / Churned / Reactivated — boxed "Comparable · Current · Δ%" Shipments and Revenue groups.
const GROUP_A = { bgcolor: 'rgba(79,70,229,0.05)' }
const GROUP_B = { bgcolor: 'rgba(13,148,136,0.05)' }
function BrandHealthTable({ rows, timeframe, trailColumns }) {
  if (!rows || !rows.length) return <Typography sx={{ fontSize: 13, color: GX_INK_400 }}>None in this window.</Typography>
  const metric = (key, header, sx, sortValue, cell, title) => ({ key, header, title, align: 'right', sortValue, cell, cellSx: sx, headerSx: sx })
  return (
    <DataTable
      rows={rows}
      rowKey={(b) => b.brandId}
      filter={(b, q) => `${b.brandName} ${b.poc}`.toLowerCase().includes(q)}
      filterPlaceholder="Filter brand / POC…"
      emptyText="No match."
      headerGroups={[
        { span: 2, align: 'left', label: (
          <Box component="span" sx={{ display: 'block', lineHeight: 1.35, textTransform: 'none', letterSpacing: 0 }}>
            <Box component="span" sx={{ display: 'block', fontSize: 10, fontWeight: 600, color: GX_INK_400 }}>PERIOD</Box>
            <Box component="span" sx={{ display: 'block', fontSize: 12, fontWeight: 600, color: GX_INK_700 }}>{timeframe}</Box>
            <Box component="span" sx={{ display: 'block', fontSize: 10, fontWeight: 400, color: GX_INK_400 }}>current vs comparable prior</Box>
          </Box>
        ) },
        { label: 'Shipments', span: 3, title: 'ProShip shipment counts — comparable prior window vs the selected window.', sx: { ...GROUP_A, color: '#4338ca' } },
        { label: 'Revenue', span: 3, title: 'ProShip revenue (₹) — comparable prior window vs the selected window.', sx: { ...GROUP_B, color: '#0f766e' } },
        { label: '', span: trailColumns.length },
      ]}
      columns={[
        { key: 'brand', header: 'Brand', title: 'Brand name (ProShip / Data → Brands).', sortValue: (b) => b.brandName, cell: (b) => <b>{b.brandName}</b> },
        { key: 'poc', header: 'Sales POC', title: 'The Data → Brands Sales POC assignment.', sortValue: (b) => b.poc, cell: (b) => b.poc },
        metric('prevShip', 'Comparable', GROUP_A, (b) => b.prevShipments, (b) => integer(b.prevShipments), 'The comparable prior window.'),
        metric('ship', 'Current', GROUP_A, (b) => b.shipments, (b) => integer(b.shipments), 'The selected window (follows the period filter).'),
        metric('shipPct', 'Δ %', GROUP_A, (b) => b.shipmentChangePct, (b) => <GrowthPct pct={b.shipmentChangePct} />, '% change: (Current − Comparable) ÷ Comparable.'),
        metric('prevRev', 'Comparable', GROUP_B, (b) => b.prevRevenue, (b) => currency(b.prevRevenue), 'The comparable prior window.'),
        metric('rev', 'Current', GROUP_B, (b) => b.revenue, (b) => currency(b.revenue), 'The selected window (follows the period filter).'),
        metric('revPct', 'Δ %', GROUP_B, (b) => b.revenueChangePct, (b) => <GrowthPct pct={b.revenueChangePct} />, '% change: (Current − Comparable) ÷ Comparable.'),
        ...trailColumns,
      ]}
    />
  )
}

const lastShipColumn = { key: 'lastShip', header: 'Last shipment', title: "The brand's most recent positive shipment, measured against the ProShip data-through date.", sortValue: (b) => b.daysSinceShipment ?? -1, cell: (b) => lastShipCell(b.lastShipment, b.daysSinceShipment) }

// ── Performance by Sales POC chart (original PocPerformanceChart) ───────────
const growthStr = (v) => (v == null ? '—' : `${v > 0 ? '+' : ''}${v}%`)
const growthColor = (v) => (v == null ? GX_INK_400 : v > 0 ? GX_POSITIVE : v < 0 ? GX_NEGATIVE : GX_INK_500)
const moodFor = (s) => (s == null ? { label: 'No prior period', color: GX_INK_400 } : s > 0 ? { label: 'Growing', color: GX_POSITIVE } : s < 0 ? { label: 'Declining', color: GX_NEGATIVE } : { label: 'Stable', color: GX_INK_400 })
const compactCurrency = (v) => `₹${Intl.NumberFormat('en-IN', { notation: 'compact', maximumFractionDigits: 1 }).format(v)}`

function PocTooltip({ active, payload }) {
  if (!active || !payload || !payload.length) return null
  const r = payload[0].payload
  const mood = moodFor(r.shipmentGrowthPct)
  const line = (k, v, color) => (
    <><Box component="span" sx={{ color: GX_INK_500 }}>{k}</Box><Box component="span" sx={{ textAlign: 'right', fontWeight: 500, color }}>{v}</Box></>
  )
  return (
    <Box sx={{ border: `1px solid ${GX_BORDER}`, bgcolor: '#fff', borderRadius: '8px', p: 1.25, fontSize: 12, boxShadow: '0 8px 20px rgba(15,23,42,0.12)' }}>
      <Box sx={{ mb: 0.5, fontWeight: 600 }}>{r.owner} <Box component="span" sx={{ fontWeight: 400, color: mood.color }}>· {mood.label}</Box></Box>
      <Box sx={{ display: 'grid', gridTemplateColumns: 'auto auto', columnGap: 1.5, rowGap: 0.25 }}>
        {line('Revenue', currency(r.revenue))}
        {line('Shipments', integer(r.shipments))}
        {line('Prev revenue', currency(r.prevRevenue))}
        {line('Prev shipments', integer(r.prevShipments))}
        {line('Revenue growth', growthStr(r.revenueGrowthPct), growthColor(r.revenueGrowthPct))}
        {line('Shipment growth', growthStr(r.shipmentGrowthPct), growthColor(r.shipmentGrowthPct))}
      </Box>
    </Box>
  )
}

function PocTick({ x, y, payload }) {
  const name = String(payload?.value ?? '')
  const short = name.length > 11 ? `${name.slice(0, 10)}…` : name
  return <g transform={`translate(${x},${y})`}><title>{name}</title><text dy={12} textAnchor="middle" style={{ fontSize: 10, fill: '#64748b' }}>{short}</text></g>
}

function PocPerformanceChart({ rows }) {
  if (!rows.length) return <Box sx={{ height: 288, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, color: GX_INK_400 }}>No Sales POC activity in this window.</Box>
  return (
    <Box sx={{ height: 288 }}>
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={rows} margin={{ top: 4, right: 8, left: 8, bottom: 18 }} barGap={4}>
          <defs>
            <linearGradient id="decPocRev" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#4F46E5" stopOpacity={0.95} /><stop offset="100%" stopColor="#818CF8" stopOpacity={0.5} /></linearGradient>
            <linearGradient id="decPocShip" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#10B981" stopOpacity={0.95} /><stop offset="100%" stopColor="#6EE7B7" stopOpacity={0.5} /></linearGradient>
          </defs>
          <CartesianGrid strokeDasharray="3 3" stroke="#eef0f6" vertical={false} />
          <XAxis dataKey="owner" interval={0} height={28} tick={<PocTick />} axisLine={{ stroke: '#e6e9f2' }} tickLine={false} />
          <YAxis yAxisId="r" tick={{ fontSize: 10, fill: '#64748b' }} tickFormatter={compactCurrency} axisLine={false} tickLine={false} />
          <YAxis yAxisId="s" orientation="right" tick={{ fontSize: 10, fill: '#64748b' }} tickFormatter={(v) => integer(v)} axisLine={false} tickLine={false} />
          <RTooltip content={<PocTooltip />} cursor={{ fill: 'rgba(79,70,229,.06)' }} />
          <Legend wrapperStyle={{ fontSize: 11 }} />
          <Bar yAxisId="r" dataKey="revenue" name="Revenue" fill="url(#decPocRev)" radius={[5, 5, 0, 0]} maxBarSize={30} />
          <Bar yAxisId="s" dataKey="shipments" name="Shipments" fill="url(#decPocShip)" radius={[5, 5, 0, 0]} maxBarSize={30} />
        </BarChart>
      </ResponsiveContainer>
    </Box>
  )
}

// ── Summary strips (Marketing / Email / LinkedIn) ───────────────────────────
function Stat({ label, value, hint, accent, active, onClick }) {
  return (
    <Box onClick={onClick} role={onClick ? 'button' : undefined} tabIndex={onClick ? 0 : undefined} aria-expanded={onClick ? Boolean(active) : undefined}
      onKeyDown={onClick ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick() } } : undefined}
      sx={{
        position: 'relative', overflow: 'hidden', borderRadius: '16px', p: 1.75, transition: 'all .2s', cursor: onClick ? 'pointer' : 'default',
        border: `1px solid ${active ? 'rgba(79,70,229,0.6)' : 'rgba(148,163,184,0.28)'}`,
        background: active ? 'linear-gradient(160deg, #eef2ff 0%, #f7f8ff 60%, #eef1fd 100%)' : 'linear-gradient(160deg, #ffffff 0%, #fbfcff 55%, #f4f6fc 100%)',
        boxShadow: '0 1px 2px rgba(15,23,42,0.06), 0 8px 18px -10px rgba(15,23,42,0.18)',
        '&::before': { content: '""', position: 'absolute', left: 0, right: 0, top: 0, height: 3, background: accent || 'linear-gradient(90deg, #6366f1, #8b5cf6)', opacity: 0.9 },
        '&:hover': onClick ? { transform: 'translateY(-2px)', borderColor: 'rgba(99,102,241,0.45)', boxShadow: '0 18px 34px -14px rgba(79,70,229,0.45)' } : undefined,
      }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, fontSize: 10, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.12em', color: GX_INK_400 }}>
        {label}
        <Box component="span" title={hint} sx={{ display: 'inline-flex', cursor: 'help' }}><InfoOutlinedIcon sx={{ fontSize: 12 }} /></Box>
        {onClick && <ChevronRightIcon sx={{ ml: 'auto', fontSize: 14, color: GX_COLORS.indigo, transition: 'transform .15s', transform: active ? 'rotate(90deg)' : 'none' }} />}
      </Box>
      <Typography sx={{ mt: 0.5, fontSize: 20, fontWeight: 600, color: '#1e293b' }}>{value}</Typography>
    </Box>
  )
}

function DealTableInline({ deals, hub, dateHeader }) {
  const anyOwner = (deals || []).some((d) => d.owner)
  return (
    <Surface>
      <DataTable
        rows={deals || []}
        rowKey={(d) => d.dealId}
        dense
        initialPageSize={10}
        emptyText="No deals."
        columns={[
          { key: 'deal', header: 'Deal', cellSx: { whiteSpace: 'normal', minWidth: 200 }, sortValue: (d) => d.dealName ?? d.brandName ?? '',
            cell: (d) => <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', columnGap: 1 }}><b>{d.dealName ?? d.brandName}</b><HsLink href={hsRecordUrl(hub, '0-3', d.dealId)} label="HubSpot" /></Box> },
          { key: 'source', header: 'Deal Source', title: 'Deal Source Sub Category — the canonical channel taxonomy.', sortValue: (d) => d.subcategory ?? '', cell: (d) => d.subcategory ?? '—' },
          ...(anyOwner ? [{ key: 'owner', header: 'Sales POC', sortValue: (d) => d.owner ?? '', cell: (d) => d.owner ?? '—' }] : []),
          { key: 'signup', header: dateHeader || 'Sign-up', title: dateHeader === 'Created' ? 'HubSpot deal create date.' : 'Deal close date (sign-up).', sortValue: (d) => d.signUpDate ?? '', cell: (d) => d.signUpDate ?? '—' },
          { key: 'orders', header: 'Orders / mo', align: 'right', title: 'Estimated monthly orders on the deal.', sortValue: (d) => d.estimatedMonthlyOrders, cell: (d) => (d.estimatedMonthlyOrders == null ? '—' : integer(d.estimatedMonthlyOrders)) },
          { key: 'mbr', header: 'Est. MBR', align: 'right', title: 'Estimated monthly business revenue (₹).', sortValue: (d) => d.estimatedMonthlyMbr, cell: (d) => (d.estimatedMonthlyMbr == null ? '—' : currency(d.estimatedMonthlyMbr)) },
          { key: 'site', header: 'Website', cellSx: { whiteSpace: 'normal', minWidth: 160 }, sortValue: (d) => d.website ?? '',
            cell: (d) => (d.website ? <a href={d.website.startsWith('http') ? d.website : `https://${d.website}`} target="_blank" rel="noopener noreferrer" style={linkSx}>{d.website}</a> : '—') },
        ]}
      />
    </Surface>
  )
}

/** ABM contacts behind the Leads-by-source ABM row (reference AbmLeadTableInline, lifted rows). */
function AbmLeadTableInline({ contacts, hub }) {
  return (
    <Surface>
      <GxPagedTable
        rows={contacts || []}
        rowKey={(c) => c.contactId}
        filter={(c, q) => `${c.name} ${c.email ?? ''} ${c.company ?? ''} ${c.campaign ?? ''}`.toLowerCase().includes(q)}
        filterPlaceholder="Filter contacts…"
        emptyText="No ABM replies in this window."
        columns={[
          { key: 'name', header: 'Contact', cellSx: { whiteSpace: 'normal', minWidth: 200 }, sortValue: (c) => c.name,
            cell: (c) => (
              <Box>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', columnGap: 1 }}><b>{c.name}</b><HsLink href={hsRecordUrl(hub, '0-1', c.contactId)} label="HubSpot" /></Box>
                {c.email && <Box sx={{ fontSize: 11, color: GX_INK_400 }}>{c.email}</Box>}
              </Box>
            ) },
          { key: 'company', header: 'Company', cellSx: { whiteSpace: 'normal', minWidth: 140 }, sortValue: (c) => c.company ?? '', cell: (c) => c.company ?? '—' },
          { key: 'replied', header: 'Replied', title: 'HubSpot "ABM Last Replied Date" — the date this lead is counted on.', sortValue: (c) => c.repliedDate, cell: (c) => c.repliedDate },
          { key: 'campaign', header: 'Campaign', cellSx: { whiteSpace: 'normal', minWidth: 180 }, sortValue: (c) => c.campaign ?? '', cell: (c) => c.campaign ?? '—' },
          { key: 'stage', header: 'Lifecycle', sortValue: (c) => c.lifecycleStage ?? '', cell: (c) => c.lifecycleStage ?? '—' },
          { key: 'sub', header: 'Sub-source', title: 'Not used for counting. Flagged when it is not ABM, so the record can be fixed in HubSpot.', sortValue: (c) => c.subSource,
            cell: (c) => (c.subSourceNeedsFix
              ? <Box component="span" title="Not set to ABM in HubSpot — worth fixing at source." sx={{ px: 0.75, py: 0.25, borderRadius: '4px', fontSize: 11, fontWeight: 600, color: '#9a3412', bgcolor: '#ffedd5' }}>{c.subSource}</Box>
              : c.subSource) },
        ]}
      />
    </Surface>
  )
}

/** One LinkedIn conversation as a drill row (reference convRow): message + HubSpot contact/company links. */
function convRow(r, i, hub) {
  const sub = [r.company, r.jobTitle, r.leader, r.sentiment, r.replyDate].filter(Boolean).join(' · ')
  const links = []
  if (hub) {
    // The reply carries no HubSpot contact id, only an email — so this is an email SEARCH.
    if (r.contactEmail) links.push({ href: `https://${hub.uiDomain}/contacts/${hub.portalId}/objects/0-1/views/all/list?query=${encodeURIComponent(r.contactEmail)}`, label: 'Contact' })
    if (r.hubspotCompanyId) links.push({ href: hsRecordUrl(hub, '0-2', r.hubspotCompanyId), label: 'Company' })
  }
  return { key: `conv-${i}`, label: r.label || r.contactEmail || `Conversation ${i + 1}`, value: null, sub: sub || null, message: r.message, links }
}

/** One or more named tabs, each a labelled breakdown (+ share); rows with deals expand to them. */
function DrillPanel({ spec, hub }) {
  const [tab, setTab] = useState(0)
  const t = spec.tabs[Math.min(tab, spec.tabs.length - 1)]
  const hasValues = t.rows.some((r) => r.value != null)
  const total = t.rows.reduce((a, r) => a + (r.value || 0), 0)
  const anyDeals = t.rows.some((r) => r.deals && r.deals.length)
  const anyContacts = t.rows.some((r) => r.contacts && r.contacts.length)
  const columns = [
    { key: 'name', header: 'Name', cellSx: { whiteSpace: 'normal', minWidth: 220 }, sortValue: (r) => r.label,
      cell: (r) => (
        <Box>
          {r.href ? <Link to={r.href} onClick={(e) => e.stopPropagation()} style={linkSx}>{r.label}</Link> : <Box component="span" sx={{ fontWeight: 500 }}>{r.label}</Box>}
          {(r.links || []).map((l) => <Box key={l.label} component="span" sx={{ ml: 1 }}><HsLink href={l.href} label={l.label} /></Box>)}
          {r.sub && <Box sx={{ fontSize: 11, color: GX_INK_400 }}>{r.sub}</Box>}
        </Box>
      ) },
    ...(t.rows.some((r) => r.message) ? [
      { key: 'message', header: 'Message', title: 'The reply as logged.', cellSx: { whiteSpace: 'normal', minWidth: 300 }, sortValue: (r) => r.message ?? '',
        cell: (r) => (r.message ? <Box component="span" sx={{ fontStyle: 'italic', color: GX_INK_500 }}>&ldquo;{r.message}&rdquo;</Box> : '—') },
    ] : []),
    ...(hasValues ? [
      { key: 'value', header: t.valueHeader, align: 'right', sortValue: (r) => r.value, cell: (r) => integer(r.value) },
      { key: 'share', header: 'Share', align: 'right', title: 'This row as a share of the rows currently listed.', sortValue: (r) => r.value,
        cell: (r) => (total > 0 && r.value != null ? `${Math.round((r.value / total) * 1000) / 10}%` : '—') },
    ] : []),
    ...(t.extraHeader ? [{ key: 'extra', header: t.extraHeader, align: 'right', sortValue: (r) => r.extra ?? '', cell: (r) => r.extra ?? '—' }] : []),
    ...(t.cols || []).map((c) => ({
      key: c.key, header: c.header, title: c.title, align: 'right', cell: (r) => r.vals?.[c.key] ?? '—',
      sortValue: (r) => { const raw = r.vals?.[c.key]; const n = raw == null ? NaN : Number(String(raw).replace(/[^0-9.-]/g, '')); return Number.isNaN(n) ? (raw ?? '') : n },
    })),
  ]
  const shared = {
    rows: t.rows, columns, dense: true, initialPageSize: 10,
    filter: (r, q) => `${r.label} ${r.sub ?? ''} ${r.message ?? ''} ${r.extra ?? ''}`.toLowerCase().includes(q),
    filterPlaceholder: 'Filter…', emptyText: 'Nothing in this window.',
  }
  return (
    <Box sx={{ mt: 1.5, p: 1.5, border: '1px solid rgba(148,163,184,0.3)', borderRadius: '12px', bgcolor: '#fff', boxShadow: '0 6px 14px -10px rgba(15,23,42,0.18)' }}>
      {spec.tabs.length > 1 && (
        <Box sx={{ mb: 1, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
          {spec.tabs.map((x, i) => (
            <Box component="button" key={x.name} onClick={() => setTab(i)}
              sx={{ border: 'none', borderRadius: '8px', px: 1.25, py: 0.5, fontSize: 12, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit',
                ...(i === tab ? { bgcolor: GX_COLORS.indigo, color: '#fff' } : { bgcolor: '#f3f4f8', color: GX_INK_500 }) }}>{x.name}</Box>
          ))}
        </Box>
      )}
      {t.note && <Typography sx={{ mb: 1, fontSize: 11, color: GX_INK_400 }}>{t.note}</Typography>}
      {anyDeals || anyContacts
        ? <ExpandableTable {...shared} rowKey={(r) => r.label} renderExpanded={(r) => (r.contacts && r.contacts.length ? <AbmLeadTableInline contacts={r.contacts} hub={hub} />
          : r.deals && r.deals.length ? <DealTableInline deals={r.deals} hub={hub} dateHeader={t.dealDateHeader} /> : null)} />
        : <DataTable {...shared} rowKey={(r) => r.key ?? r.label} />}
    </Box>
  )
}

function StatStrip({ items, hub }) {
  const [open, setOpen] = useState(null)
  const openItem = items.find((i) => i.key === open && i.drill)
  return (
    <Box>
      <Box sx={{ display: 'grid', gap: 1.5, gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: 'repeat(3, 1fr)', lg: 'repeat(6, 1fr)' } }}>
        {items.map((it) => (
          <Stat key={it.key} label={it.label} value={it.value} hint={it.hint} accent={it.accent} active={open === it.key}
            onClick={it.drill ? () => setOpen(open === it.key ? null : it.key) : undefined} />
        ))}
      </Box>
      {openItem && <DrillPanel key={openItem.key} spec={openItem.drill} hub={hub} />}
    </Box>
  )
}

// ── HubSpot gaps ────────────────────────────────────────────────────────────
const COMPANY_GAP_TILES = [
  ['Unowned', 'unowned'], ['No industry', 'industry'], ['No location', 'location'], ['No revenue', 'revenue'],
  ['No MSME', 'msme'], ['Support-team owner', 'nonSalesOwner'], ['Owner unclassified', 'ownerUnclassified'],
]
const GAP_COLS = [
  { key: 'hotIdle', label: 'Hot idle', arrs: ['hotStale'], cols: 'idle' },
  { key: 'warmIdle', label: 'Warm idle', arrs: ['warmStale'], cols: 'idle' },
  { key: 'amountNeMbr', label: 'Amount≠MBR', arrs: ['amountMbrMismatch'], cols: 'amount' },
  { key: 'mktStale', label: 'Mktg idle', arrs: ['mktHotStale', 'mktWarmStale'], cols: 'idle' },
  { key: 'expectedClosePastToday', label: 'Close overdue', arrs: ['expectedClosePast'], cols: 'close' },
  { key: 'temperatureBlank', label: 'Temp blank', arrs: ['temperatureBlank'], cols: 'temp' },
  { key: 'coldOrHibernating', label: 'Cold/Hib.', arrs: ['coldHibernating'], cols: 'idle' },
]

function CompanyGapList({ gap, hub }) {
  const { data, isFetching, error } = useGetHubspotCompanyGapQuery(gap)
  if (isFetching && !data) return <Clean text="Loading companies…" />
  if (error) return <Clean text={`Failed to load — ${error.message || `HTTP ${error.status}`}`} />
  return (
    <DataTable
      rows={data?.rows || []}
      rowKey={(c) => c.id}
      emptyText="None."
      filter={(c, q) => `${c.name ?? ''} ${c.owner ?? ''} ${c.industry ?? ''} ${c.city ?? ''}`.toLowerCase().includes(q)}
      filterPlaceholder="Filter company / owner…"
      columns={[
        { key: 'company', header: 'Company', title: 'Company name.', sortValue: (c) => c.name ?? '', cell: (c) => <b>{c.name || '—'}</b> },
        { key: 'owner', header: 'Owner', title: 'Raw HubSpot owner — may not be a classified Sales POC.', sortValue: (c) => c.owner ?? '', cell: (c) => c.owner ?? '—' },
        { key: 'team', header: 'Team', title: "The Sales POC's team tag (Data → Sales POCs).", sortValue: (c) => c.team ?? '', cell: (c) => c.team ?? '—' },
        { key: 'industry', header: 'Industry', title: 'Company industry (HubSpot).', sortValue: (c) => c.industry ?? '', cell: (c) => c.industry ?? '—' },
        { key: 'city', header: 'City', title: 'Company city (HubSpot).', sortValue: (c) => c.city ?? '', cell: (c) => c.city ?? '—' },
        { key: 'country', header: 'Country', title: 'Company country (HubSpot).', sortValue: (c) => c.country ?? '', cell: (c) => c.country ?? '—' },
        { key: 'hubspot', header: 'HubSpot', title: 'Opens the record in HubSpot.', cell: (c) => <HsLink href={hsRecordUrl(hub, '0-2', c.id)} label="Company" /> },
      ]}
    />
  )
}

function GapDealTable({ rows, cols, hub }) {
  const columns = [{ key: 'deal', header: 'Deal', title: 'HubSpot deal name.', sortValue: (d) => d.name ?? '', cell: (d) => <b>{d.name}</b> }]
  if (cols === 'idle') columns.push(
    { key: 'lastAct', header: 'Last activity', title: "The deal's last activity date (HubSpot).", sortValue: (d) => d.lastActivityAt ?? '', cell: (d) => (d.lastActivityAt ? shortDate(d.lastActivityAt) : 'Never') },
    { key: 'daysIdle', header: 'Days idle', align: 'right', sortValue: (d) => d.daysSinceActivity ?? -1, cell: (d) => (d.daysSinceActivity == null ? 'Never' : integer(d.daysSinceActivity)) },
  )
  if (cols === 'amount') columns.push(
    { key: 'amount', header: 'Amount', title: 'HubSpot deal Amount (₹).', align: 'right', sortValue: (d) => d.amount, cell: (d) => currency(d.amount) },
    { key: 'mbr', header: 'Est MBR', title: 'Estimated Monthly Billing Revenue (₹/month) on the deal.', align: 'right', sortValue: (d) => d.estimatedMbr, cell: (d) => currency(d.estimatedMbr) },
  )
  if (cols === 'close' || cols === 'temp') columns.push(
    { key: 'pipeline', header: 'Pipeline', title: 'HubSpot pipeline.', sortValue: (d) => d.pipeline ?? '', cell: (d) => d.pipeline ?? '—' },
    { key: 'stage', header: 'Stage', title: 'Current HubSpot pipeline stage.', sortValue: (d) => d.stage ?? '', cell: (d) => d.stage ?? '—' },
  )
  if (cols === 'close') columns.push({ key: 'close', header: 'Expected close', sortValue: (d) => d.expectedCloseDate ?? '', cell: (d) => shortDate(d.expectedCloseDate) })
  columns.push({ key: 'hubspot', header: 'HubSpot', title: 'Opens the record in HubSpot.', cell: (d) => <HsLink href={hsRecordUrl(hub, '0-3', d.id)} label="Deal" /> })
  return <DataTable rows={rows} rowKey={(d, i) => `${d.id}-${i}`} emptyText="None." columns={columns} />
}

function HubspotGapsBody({ hg, hub }) {
  const [companyGap, setCompanyGap] = useState(null)
  const [gapDrill, setGapDrill] = useState(null) // { owner, gap }
  const counts = hg.companyGapCounts || {}
  const gKey = (g) => (g.personId != null ? `p${g.personId}` : g.owner)
  return (
    <>
      <Box sx={{ mb: 2, display: 'grid', gap: 1, gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: 'repeat(4, 1fr)', lg: 'repeat(7, 1fr)' } }}>
        {COMPANY_GAP_TILES.map(([label, k]) => {
          const n = counts[k] || 0; const on = companyGap === k
          return (
            <Box component="button" key={k} onClick={() => setCompanyGap(on ? null : k)}
              sx={{ textAlign: 'left', borderRadius: '12px', px: 1.5, py: 1, cursor: 'pointer', fontFamily: 'inherit', transition: 'all .15s',
                border: `1px solid ${on ? '#0891b2' : GX_BORDER}`, bgcolor: on ? 'rgba(8,145,178,0.06)' : 'transparent' }}>
              <Typography sx={{ fontSize: 18, fontWeight: 700, color: n > 0 ? '#b45309' : GX_INK_900 }}>{integer(n)}</Typography>
              <Typography sx={{ fontSize: 12, color: GX_INK_500 }}>{label}</Typography>
            </Box>
          )
        })}
      </Box>
      {companyGap && <Box sx={{ mb: 2 }}><CompanyGapList gap={companyGap} hub={hub} /></Box>}
      <DrillLabel>Sales POC-wise deal gaps · click a number</DrillLabel>
      <DataTable
        rows={hg.pocGaps || []}
        rowKey={gKey}
        filter={(g, q) => `${g.owner} ${g.team ?? ''}`.toLowerCase().includes(q)}
        filterPlaceholder="Filter POC / team…"
        emptyText="No open deal gaps."
        isRowActive={(g) => gKey(g) === gapDrill?.owner}
        renderExpanded={(g) => {
          if (gapDrill?.owner !== gKey(g)) return null
          const def = GAP_COLS.find((c) => c.key === gapDrill.gap)
          if (!def) return null
          // Match on the stable owner id (name only as fallback) so same-name owners never mix.
          const rows = def.arrs.flatMap((a) => (hg.deals?.[a] || []))
            .filter((d) => (g.personId != null ? d.ownerPersonId === g.personId : d.ownerPersonId == null && d.owner === g.owner))
          return <Surface><GapDealTable rows={rows} cols={def.cols} hub={hub} /></Surface>
        }}
        columns={[
          { key: 'owner', header: 'Sales POC', title: "The open deal's HubSpot owner.", sortValue: (g) => g.owner, cell: (g) => <PocName personId={g.personId} name={g.owner} /> },
          { key: 'team', header: 'Team', title: "The Sales POC's team tag (Data → Sales POCs).", sortValue: (g) => g.team ?? '', cell: (g) => g.team ?? '—' },
          ...GAP_COLS.filter((c) => c.key !== 'coldOrHibernating').map((c) => ({
            key: c.key, header: c.label, align: 'right', sortValue: (g) => g[c.key],
            cell: (g) => { const k = gKey(g); const active = gapDrill?.owner === k && gapDrill?.gap === c.key; return <CountButton value={g[c.key]} active={active} onClick={() => setGapDrill(active ? null : { owner: k, gap: c.key })} /> },
          })),
          { key: 'total', header: 'Total', title: 'Counted gaps only — Mktg idle (subset) and Cold/Hib. (parked book) are not added.', align: 'right', sortValue: (g) => g.total,
            cell: (g) => <b title="Sum of Hot idle + Warm idle + Amount≠MBR + Close overdue + Temp blank. Mktg idle is a subset of Hot/Warm idle and is not re-added.">{integer(g.total)}</b> },
          { key: 'coldOrHibernating', header: 'Cold/Hib.', title: 'Open deals parked as Cold/Hibernating — a stale-pipeline signal, NOT counted in Total. Hover a count for the ₹/month parked.', align: 'right', sortValue: (g) => g.coldOrHibernating,
            cell: (g) => { const k = gKey(g); const active = gapDrill?.owner === k && gapDrill?.gap === 'coldOrHibernating'; return <span title={g.coldHibernatingMbr > 0 ? `${currency(g.coldHibernatingMbr)}/month Est. MBR parked` : undefined}><CountButton value={g.coldOrHibernating} active={active} onClick={() => setGapDrill(active ? null : { owner: k, gap: 'coldOrHibernating' })} /></span> } },
        ]}
      />
    </>
  )
}

// ── Page ────────────────────────────────────────────────────────────────────
export default function DecisionInsightsPage() {
  const [period, setPeriod] = useState('mtd')
  const [customFrom, setCustomFrom] = useState('')
  const [customTo, setCustomTo] = useState('')
  const [lateBucket, setLateBucket] = useState(null)
  const [ccView, setCcView] = useState('poc')
  const [openCcGroup, setOpenCcGroup] = useState(null)
  const [pocDrill, setPocDrill] = useState(null) // { key, kind: 'brands' | 'closed' }
  const [openInd, setOpenInd] = useState(null)
  const [openSub, setOpenSub] = useState(null)
  const customReady = period !== 'custom' || Boolean(customFrom && customTo)

  const { data, isLoading, error } = useGetDecisionInsightsQuery(
    { period, from: period === 'custom' ? customFrom : undefined, to: period === 'custom' ? customTo : undefined },
    { skip: !customReady },
  )
  const { data: runsData } = useListIngestRunsQuery()
  const hub = data?.hubspot
  const pw = data?.proshipWindow
  const tf = pw ? `${shortDate(pw.from)} – ${shortDate(pw.to)}` : (data?.window?.label ?? '')
  const cc = data?.customersClosed
  const ccGroups = cc ? (ccView === 'poc' ? cc.byPoc : ccView === 'pipeline' ? cc.byPipeline : cc.byDealSource) || [] : []
  const lateDeals = useMemo(() => {
    const d = data?.lateOnboarding?.rows || []
    return lateBucket ? d.filter((x) => x.bucket === lateBucket) : d
  }, [data, lateBucket])

  // Industry → sub-category → brand tree, flattened to the currently visible nodes.
  const catRows = useMemo(() => {
    const out = []
    for (const ind of data?.categoryOfBrands?.rows || []) {
      const imatch = `${ind.industry} ${ind.subcategories.map((s) => s.subcategory).join(' ')}`.toLowerCase()
      out.push({ level: 'industry', key: ind.industry, imatch, ind })
      if (openInd !== ind.industry) continue
      for (const s of ind.subcategories) {
        const sk = `${ind.industry}|${s.subcategory}`
        out.push({ level: 'sub', key: sk, imatch, sub: s })
        if (openSub === sk) for (const b of s.brands) out.push({ level: 'brand', key: `${sk}|${b.brandId}`, imatch, brand: b })
      }
    }
    return out
  }, [data, openInd, openSub])

  const pocKey = (p) => (p.personId != null ? `p${p.personId}` : p.owner)
  const ms = data?.marketingSummary
  const em = data?.emailMarketingSummary
  const la = data?.linkedinAbmSummary
  const hg = data?.hubspotGaps

  return (
    <Box sx={GX_PAGE_SX}>
      <Box sx={{ mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.75 }}>
          <LightbulbIcon sx={{ fontSize: 15, color: '#f59e0b' }} />
          <Typography sx={{ fontSize: 12, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.14em', color: '#f59e0b' }}>Decisions &amp; Insights</Typography>
        </Box>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'flex-end', justifyContent: 'space-between', gap: 2 }}>
          <Box>
            <Typography sx={{ fontSize: 26, fontWeight: 700, color: GX_INK_900 }}>Decision / Insights</Typography>
            <Typography sx={{ fontSize: 14, color: GX_INK_500, mt: 0.5 }}>
              Where the business needs a decision — customers closed, onboarding gaps, brand momentum, POC performance, category mix, and CRM hygiene.
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
            <Select size="small" value={period} onChange={(e) => setPeriod(e.target.value)} sx={{ minWidth: 140, fontSize: 13, bgcolor: '#fff' }}>
              {PERIODS.map((p) => <MenuItem key={p.value} value={p.value}>{p.label}</MenuItem>)}
            </Select>
            {period === 'custom' && (
              <>
                <TextField size="small" type="date" value={customFrom} onChange={(e) => setCustomFrom(e.target.value)} />
                <Typography sx={{ color: GX_INK_400 }}>→</Typography>
                <TextField size="small" type="date" value={customTo} onChange={(e) => setCustomTo(e.target.value)} />
              </>
            )}
            {data?.window && (
              <GxPill color={GX_COLORS.indigo} icon={<CalendarTodayIcon sx={{ fontSize: 12 }} />}>
                Showing: {data.window.label}{pw ? ` · ProShip ${shortDate(pw.from)} – ${shortDate(pw.to)}` : ''}
              </GxPill>
            )}
          </Box>
        </Box>
        <Box sx={{ mt: 1.5 }}><GxSyncStatusBar runs={runsData?.runs || []} labels={SYNC_LABELS} /></Box>
        {pw?.dataThrough && <Typography sx={{ fontSize: 11, color: GX_INK_400 }}>As of ProShip: {shortDate(pw.dataThrough)} — churn and days-since-shipment are measured to this date.</Typography>}
      </Box>

      {error && (
        <Box sx={{ border: '1px solid #fecdd3', bgcolor: '#fff1f2', color: '#be123c', borderRadius: '16px', p: 2, mb: 2, fontSize: 13 }}>
          Failed to load — {error?.data?.message || error?.message || `HTTP ${error?.status}`}
        </Box>
      )}
      {isLoading && <Paper variant="outlined" sx={{ ...SHELL_SX, p: 4, textAlign: 'center', fontSize: 13, color: GX_INK_400 }}>Loading…</Paper>}

      {data && cc && (
        <>
          <SectionGroup title="Customers & Performance" icon={<EmojiEventsIcon sx={{ fontSize: 16 }} />} color="#4f46e5"
            subtitle="Won customers and what happens next — onboarding speed, brand health, and who owns them.">
            {/* Customers · Closed */}
            <Section title="Customers · Closed" subtitle="Pick a lens: Sales POC, Pipeline, or Deal Source — click a row to drill into deals"
              scopeLabel="All Pipelines" icon={<EmojiEventsIcon sx={{ fontSize: 18 }} />} color="#4f46e5" count={cc.total} sources={['HubSpot', 'ProShip']}>
              <Box sx={{ mb: 2, display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
                {[['Customers · Closed', integer(cc.total)], ['Live', integer(cc.liveTotal)], ['Revenue potential', currency(cc.revenuePotentialTotal)], ['Actual revenue', currency(cc.actualRevenueTotal)]].map(([l, v]) => (
                  <Box key={l} sx={{ border: `1px solid ${GX_BORDER}`, borderRadius: '12px', px: 2, py: 1 }}>
                    <Typography sx={{ fontSize: 20, fontWeight: 700 }}>{v}</Typography>
                    <Typography sx={{ fontSize: 12, color: GX_INK_500 }}>{l}</Typography>
                  </Box>
                ))}
              </Box>
              <Box sx={{ mb: 2, display: 'grid', gap: 1.5, gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' } }}>
                {[
                  { key: 'poc', label: 'Sales POC Wise', icon: <GroupsIcon sx={{ fontSize: 16 }} />, n: (cc.byPoc || []).length, sub: 'by deal owner' },
                  { key: 'pipeline', label: 'Pipeline Wise', icon: <LayersIcon sx={{ fontSize: 16 }} />, n: (cc.byPipeline || []).length, sub: 'by HubSpot pipeline' },
                  { key: 'source', label: 'Deal Source Wise', icon: <CampaignIcon sx={{ fontSize: 16 }} />, n: (cc.byDealSource || []).length, sub: 'by marketing sub-category' },
                ].map((x) => {
                  const on = ccView === x.key
                  return (
                    <Box component="button" key={x.key} aria-pressed={on} onClick={() => { setCcView(x.key); setOpenCcGroup(null) }}
                      sx={{ textAlign: 'left', borderRadius: '12px', p: 1.5, cursor: 'pointer', fontFamily: 'inherit',
                        border: `1px solid ${on ? '#4f46e5' : GX_BORDER}`, bgcolor: on ? 'rgba(79,70,229,0.06)' : 'transparent', boxShadow: on ? '0 0 0 1px #4f46e5 inset' : 'none' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, fontSize: 14, fontWeight: 600, color: on ? '#4f46e5' : '#1e293b' }}>{x.icon} {x.label}</Box>
                      <Typography sx={{ mt: 0.5, fontSize: 12, color: GX_INK_400 }}>{integer(x.n)} groups · {x.sub}</Typography>
                    </Box>
                  )
                })}
              </Box>
              <DataTable
                rows={ccGroups}
                rowKey={(g) => g.key || g.label}
                filter={(g, q) => g.label.toLowerCase().includes(q)}
                filterPlaceholder={ccView === 'poc' ? 'Filter Sales POC…' : ccView === 'pipeline' ? 'Filter pipeline…' : 'Filter deal source…'}
                emptyText="No customers closed in this window."
                onRowClick={(g) => setOpenCcGroup((cur) => (cur === (g.key || g.label) ? null : (g.key || g.label)))}
                isRowActive={(g) => (g.key || g.label) === openCcGroup}
                renderExpanded={(g) => (
                  <Surface>
                    <DrillLabel>{g.label} · {integer(g.count)} deals</DrillLabel>
                    <ClosedDealTable deals={g.deals} hub={hub} />
                  </Surface>
                )}
                columns={[
                  { key: 'label', header: ccView === 'poc' ? 'Sales POC' : ccView === 'pipeline' ? 'Pipeline' : 'Deal Source', sortValue: (g) => g.label,
                    cell: (g) => (ccView === 'poc' ? <PocName personId={g.personId} name={g.label} /> : <b>{g.label}</b>) },
                  ...(ccView === 'poc' ? [{ key: 'team', header: 'Team', title: "The Sales POC's team tag.", sortValue: (g) => g.team ?? '', cell: (g) => g.team ?? '—' }] : []),
                  { key: 'count', header: 'Customers · Closed', title: 'Deals won with a CLOSE date in the window, any create date.', align: 'right', sortValue: (g) => g.count, cell: (g) => integer(g.count) },
                  { key: 'live', header: 'Live', title: 'Closed customers whose brand has shipped at least once.', align: 'right', sortValue: (g) => g.live, cell: (g) => integer(g.live) },
                  { key: 'revpot', header: 'Rev. potential', title: 'Σ Estimated MBR (₹/month) of the closed deals.', align: 'right', sortValue: (g) => g.revenuePotential, cell: (g) => currency(g.revenuePotential) },
                  { key: 'actrev', header: 'Actual rev.', title: 'Lifetime ProShip revenue since these brands went live.', align: 'right', sortValue: (g) => g.actualRevenue, cell: (g) => currency(g.actualRevenue) },
                ]}
              />
            </Section>

            {/* Late Onboarding */}
            <Section title="Late Onboarding — TAT" subtitle="Closed-won by time-to-first-shipment · badge counts every deal that took > 7 days to go live · click a bucket to filter"
              scopeLabel={data.pipelineLabel || 'D2C Freight'} icon={<TimerIcon sx={{ fontSize: 18 }} />} color="#7c3aed"
              count={data.lateOnboarding?.lateCount ?? 0} sources={['HubSpot', 'ProShip']}>
              <Box sx={{ mb: 2, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {(data.lateOnboarding?.buckets || []).map((b) => {
                  const on = lateBucket === b.key; const bad = b.key === 'never' || b.key === 'b60plus'
                  return (
                    <Box component="button" key={b.key} onClick={() => setLateBucket(on ? null : b.key)}
                      sx={{ textAlign: 'left', minWidth: 116, borderRadius: '12px', px: 1.5, py: 1, cursor: 'pointer', fontFamily: 'inherit',
                        border: `1px solid ${on ? '#7c3aed' : GX_BORDER}`, bgcolor: on ? 'rgba(124,58,237,0.06)' : 'transparent' }}>
                      <Typography sx={{ fontSize: 18, fontWeight: 700, color: bad ? GX_NEGATIVE : GX_INK_900 }}>{integer(b.count)}</Typography>
                      <Typography sx={{ fontSize: 12, fontWeight: 500, color: GX_INK_500 }}>{b.label}</Typography>
                      <Typography sx={{ mt: 0.25, fontSize: 11, color: GX_INK_400 }}>{currency(b.revenuePotential)} pot · {integer(b.actualShipments)} act. ship</Typography>
                    </Box>
                  )
                })}
              </Box>
              <DataTable
                rows={lateDeals}
                rowKey={(d) => d.dealId}
                filter={(d, q) => `${d.dealName ?? ''} ${d.poc ?? ''} ${d.brandName ?? ''} ${d.proshipName ?? ''}`.toLowerCase().includes(q)}
                filterPlaceholder="Filter deal / POC / brand…"
                emptyText="No deals in this bucket."
                columns={[
                  { key: 'deal', header: 'Deal', title: 'HubSpot deal name.', sortValue: (d) => d.dealName ?? '', cell: (d) => <b>{d.dealName}</b> },
                  { key: 'poc', header: 'Sales POC', title: 'Deal owner (Sales POC) on HubSpot.', sortValue: (d) => d.poc ?? '', cell: (d) => <PocName personId={d.personId} name={d.poc} bold={false} /> },
                  { key: 'proship', header: 'ProShip name', title: 'Brand name as it appears in ProShip.', sortValue: (d) => d.proshipName ?? '', cell: (d) => d.proshipName ?? '—' },
                  { key: 'closed', header: 'Closed', title: 'Deal close date (HubSpot).', sortValue: (d) => d.closedDate ?? '', cell: (d) => shortDate(d.closedDate) },
                  { key: 'firstShip', header: 'First shipment', title: "The brand's first-ever positive ProShip shipment date.", sortValue: (d) => d.firstShipmentDate ?? '', cell: (d) => shortDate(d.firstShipmentDate) },
                  { key: 'tat', header: 'TAT (days)', title: "Turn-around time — days from deal close to the brand's first shipment.", align: 'right', sortValue: (d) => d.tatDays ?? -1, cell: (d) => (d.tatDays == null ? '—' : integer(d.tatDays)) },
                  { key: 'revpot', header: 'Rev. potential', title: 'Estimated MBR (₹/month) from the deal.', align: 'right', sortValue: (d) => d.revenuePotential, cell: (d) => currency(d.revenuePotential) },
                  { key: 'shippot', header: 'Ship. potential', title: 'Expected monthly shipments from the deal.', align: 'right', sortValue: (d) => d.shipmentPotential, cell: (d) => integer(d.shipmentPotential) },
                  { key: 'actrev', header: 'Actual rev.', title: 'Lifetime ProShip revenue since the brand went live.', align: 'right', sortValue: (d) => d.actualRevenue, cell: (d) => currency(d.actualRevenue) },
                  { key: 'actship', header: 'Actual ship.', title: 'Lifetime ProShip shipments since the brand went live.', align: 'right', sortValue: (d) => d.actualShipments, cell: (d) => integer(d.actualShipments) },
                  { key: 'status', header: 'Status', title: 'Live once the brand has shipped at least once.', sortValue: (d) => (d.live ? 1 : 0), cell: (d) => <LiveBadge live={d.live} /> },
                  { key: 'hubspot', header: 'HubSpot', title: 'Opens the record in HubSpot.', cell: (d) => <HsLink href={hsRecordUrl(hub, '0-3', d.dealId)} label="Deal" /> },
                ]}
              />
            </Section>

            <Section title="Declining Brands" subtitle="Revenue below the comparable prior window" icon={<TrendingDownIcon sx={{ fontSize: 18 }} />} color="#d97706"
              count={data.declining?.count ?? 0} sources={['ProShip']}>
              <BrandHealthTable rows={data.declining?.rows} timeframe={tf} trailColumns={[lastShipColumn]} />
            </Section>
            <Section title="Churned Brands" subtitle="No shipment in the last ~30 days · most recent last-shipment first" icon={<PersonOffIcon sx={{ fontSize: 18 }} />} color="#e11d48"
              count={data.churned?.count ?? 0} sources={['ProShip']}>
              <BrandHealthTable rows={data.churned?.rows} timeframe={tf} trailColumns={[lastShipColumn]} />
            </Section>
            <Section title="Reactivated Brands" subtitle="Resumed shipping after a full dormant prior window" icon={<RepeatIcon sx={{ fontSize: 18 }} />} color="#059669"
              count={data.reactivated?.count ?? 0} sources={['ProShip']}>
              <BrandHealthTable rows={data.reactivated?.rows} timeframe={tf} trailColumns={[
                { key: 'firstShip', header: 'First shipment', title: "The brand's first-ever positive ProShip shipment date.", sortValue: (b) => b.firstShipmentAt ?? '', cell: (b) => shortDate(b.firstShipmentAt) },
                { key: 'lastShip', header: 'Last shipment', title: "The brand's most recent positive shipment.", sortValue: (b) => b.lastShipment ?? '', cell: (b) => shortDate(b.lastShipment) },
              ]} />
            </Section>

            {/* Sales POC Performance */}
            <Section title="Sales POC Performance" subtitle="Shipments · Revenue · Customers · Closed (all pipelines) · Active Brands — the last two are clickable"
              icon={<GroupsIcon sx={{ fontSize: 18 }} />} color="#0ea5e9" count={(data.pocPerformance || []).length} sources={['HubSpot', 'ProShip']}>
              <Box sx={{ mb: 2 }}>
                <Typography sx={{ fontSize: 14, fontWeight: 600, color: GX_INK_700 }}>Performance by Sales POC</Typography>
                <Typography sx={{ mb: 1, fontSize: 12, color: GX_INK_400 }}>Revenue and shipments per POC, with growth against the comparable prior period on hover. Top 12 by revenue.</Typography>
                <PocPerformanceChart rows={(data.pocPerformance || []).slice(0, 12)} />
              </Box>
              <DataTable
                rows={data.pocPerformance || []}
                rowKey={pocKey}
                filter={(p, q) => `${p.owner} ${p.team ?? ''}`.toLowerCase().includes(q)}
                filterPlaceholder="Filter Sales POC / team…"
                emptyText="No POC activity in this window."
                isRowActive={(p) => pocKey(p) === pocDrill?.key}
                renderExpanded={(p) => {
                  if (pocDrill?.key !== pocKey(p)) return null
                  if (pocDrill.kind === 'closed') return <Surface><DrillLabel>Customers · Closed by {p.owner}</DrillLabel><ClosedDealTable deals={p.closedDeals} hub={hub} /></Surface>
                  return <Surface><DrillLabel>Active brands for {p.owner}</DrillLabel><ActiveBrandsTable brands={p.brands} /></Surface>
                }}
                columns={[
                  { key: 'poc', header: 'Sales POC', title: 'The Data → Brands Sales POC assignment (ProShip) / deal owner (HubSpot).', sortValue: (p) => p.owner, cell: (p) => <PocName personId={p.personId} name={p.owner} /> },
                  { key: 'team', header: 'Team', title: "The Sales POC's team tag (Data → Sales POCs).", sortValue: (p) => p.team ?? '', cell: (p) => p.team ?? '—' },
                  { key: 'shipments', header: 'Shipments', title: 'ProShip shipments in the selected window.', align: 'right', sortValue: (p) => p.shipments, cell: (p) => integer(p.shipments) },
                  { key: 'shipG', header: 'Ship. growth', title: 'Shipments vs the comparable prior window (blank when the prior window had none).', align: 'right', sortValue: (p) => p.shipmentGrowthPct, cell: (p) => <GrowthPct pct={p.shipmentGrowthPct} /> },
                  { key: 'revenue', header: 'Revenue', title: 'ProShip revenue (₹) in the selected window.', align: 'right', sortValue: (p) => p.revenue, cell: (p) => currency(p.revenue) },
                  { key: 'revG', header: 'Rev. growth', title: 'Revenue vs the comparable prior window (blank when the prior window had none).', align: 'right', sortValue: (p) => p.revenueGrowthPct, cell: (p) => <GrowthPct pct={p.revenueGrowthPct} /> },
                  { key: 'cc', header: 'Customers · Closed', title: 'Deals won with a CLOSE date in the window, any create date.', align: 'right', sortValue: (p) => p.customersClosed,
                    cell: (p) => { const active = pocDrill?.key === pocKey(p) && pocDrill.kind === 'closed'; return <CountButton value={p.customersClosed} active={active} onClick={() => setPocDrill(active ? null : { key: pocKey(p), kind: 'closed' })} /> } },
                  { key: 'brands', header: 'Active Brands', title: 'Distinct brands with ≥1 positive shipment in the window.', align: 'right', sortValue: (p) => p.activeBrands,
                    cell: (p) => { const active = pocDrill?.key === pocKey(p) && pocDrill.kind === 'brands'; return <CountButton value={p.activeBrands} active={active} onClick={() => setPocDrill(active ? null : { key: pocKey(p), kind: 'brands' })} /> } },
                ]}
              />
            </Section>
          </SectionGroup>

          <SectionGroup title="Marketing" icon={<CampaignIcon sx={{ fontSize: 16 }} />} color="#8b5cf6" subtitle="Demand generation across every channel — the funnel plus both ABM motions.">
            <Section title="Marketing" icon={<CampaignIcon sx={{ fontSize: 18 }} />} color="#8b5cf6" sources={['HubSpot']}
              subtitle={`Leads → opportunities → customers, with unit economics. Definitions and values come from the Marketing dashboard${ms?.windowLabel ? ` · ${ms.windowLabel}` : ''}.`}>
              {!ms ? <Clean text="Couldn't load the Marketing summary right now." /> : (
                <StatStrip hub={hub} items={[
                  { key: 'leads', label: 'Leads', value: integer(ms.leads), accent: 'linear-gradient(90deg,#6366f1,#818cf8)',
                    hint: 'Contacts created in the window with Lead Source = Marketing (ABM counted on reply date). Click to see which sources produced them.',
                    drill: { tabs: [{ name: 'By source', valueHeader: 'Leads', extraHeader: 'Cost / lead',
                      note: 'Leads are contact-grain and carry no Sales POC or pipeline — source is the only honest split.',
                      rows: (ms.leadsBySource || []).map((r) => ({ label: r.label, value: r.value, extra: r.cost == null ? '—' : currency(r.cost),
                        // Only ABM expands — its contact-grain cohort is lifted from the Marketing dashboard.
                        contacts: r.label === 'ABM' ? (ms.abmLeads?.rows || []) : undefined })) }] } },
                  { key: 'opps', label: 'Opportunities', value: integer(ms.opportunities), accent: 'linear-gradient(90deg,#8b5cf6,#a855f7)',
                    hint: 'Marketing-sourced deals created in the window. Click for who owns them and where they came from.',
                    drill: { tabs: [
                      { name: 'Sales POC-wise', valueHeader: 'Opportunities', extraHeader: 'WIP · Total', dealDateHeader: 'Created',
                        note: 'WIP · Total is every open deal this POC holds; WIP · Hot+Warm is the live subset (HubSpot Deal Type Hot or Warm). Click a row for the deals; click the name for their dashboard.',
                        cols: [{ key: 'hw', header: 'WIP · Hot+Warm', title: 'Open deals marked Hot or Warm — a subset of WIP · Total.' }],
                        rows: (ms.opportunitiesByPoc || []).map((r) => ({ label: r.label, value: r.value, sub: r.team, extra: integer(r.wipTotal), vals: { hw: integer(r.wipHotWarm) }, href: pocHref(r.personId), deals: r.deals })) },
                      { name: 'Funnel-wise', valueHeader: 'Opportunities', extraHeader: 'Cost / opp', dealDateHeader: 'Created',
                        note: 'Click a source to see the deals it produced in this window.',
                        rows: (ms.opportunitiesBySource || []).map((r) => ({ label: r.label, value: r.value, extra: r.cost == null ? '—' : currency(r.cost), deals: (ms.opportunityDealsBySource || {})[r.label] })) },
                    ] } },
                  { key: 'cust', label: 'Customers · Closed', value: integer(ms.customersClosed), accent: 'linear-gradient(90deg,#059669,#34d399)',
                    hint: 'Marketing deals reaching Won/Onboarded with a CLOSE date in the window, regardless of when created.',
                    drill: { tabs: [
                      { name: 'Sales POC-wise', valueHeader: 'Customers', dealDateHeader: 'Sign-up',
                        note: 'Win % · Decision = won ÷ (won + lost). Win % · Overall = won ÷ (won + lost + open Hot+Warm), so a POC still carrying a lot of active pipeline cannot read 100%. Click a Sales POC for the deals behind the number.',
                        cols: [
                          { key: 'wd', header: 'Win % · Decision', title: 'Won ÷ (won + lost) in the window.' },
                          { key: 'wo', header: 'Win % · Overall', title: 'Won ÷ (won + lost + open Hot/Warm deals).' },
                        ],
                        rows: (ms.customersByPoc || []).map((r) => ({ label: r.label, value: r.value, sub: r.team, href: pocHref(r.personId), deals: r.deals,
                          vals: { wd: r.winPctDecision == null ? '—' : `${r.winPctDecision}%`, wo: r.winPctOverall == null ? '—' : `${r.winPctOverall}%` } })) },
                      { name: 'Funnel-wise', valueHeader: 'Customers', extraHeader: 'Cost / customer', note: 'Click a source to see its closed-won deals.',
                        rows: (ms.customersBySource || []).map((r) => ({ label: r.label, value: r.value, extra: r.cost == null ? '—' : currency(r.cost), deals: (ms.customerDealsBySource || {})[r.label] })) },
                    ] } },
                  { key: 'cpl', label: 'Cost / Lead', value: currency(ms.costPerLead), accent: 'linear-gradient(90deg,#f59e0b,#fbbf24)', hint: 'Window ad spend ÷ leads. Blank until spend is entered in Data → Marketing Budget — never assumed to be zero.' },
                  { key: 'cpo', label: 'Cost / Opportunity', value: currency(ms.costPerOpportunity), accent: 'linear-gradient(90deg,#f59e0b,#fbbf24)', hint: 'Window ad spend ÷ opportunities.' },
                  { key: 'cpc', label: 'Cost / Customer', value: currency(ms.costPerCustomer), accent: 'linear-gradient(90deg,#f59e0b,#fbbf24)', hint: 'Window ad spend ÷ Customers · Closed.' },
                ]} />
              )}
            </Section>

            <Section title="ABM · Email Marketing" icon={<MailOutlineIcon sx={{ fontSize: 18 }} />} color="#0891b2" sources={['HubSpot']}
              subtitle={`Outreach reach and engagement, all contact-grain. Values come from the Email Marketing dashboard${em?.windowLabel ? ` · ${em.windowLabel}` : ''}.`}>
              {!em ? <Clean text="Couldn't load the Email Marketing summary right now." /> : (() => {
                const seg = (metric, header) => ({ tabs: [
                  { name: 'By category', valueHeader: header, rows: (em.byCategory || []).map((r) => ({ label: r.label, value: r[metric] ?? null })) },
                  { name: 'By sales leader', valueHeader: header, rows: (em.byLeader || []).map((r) => ({ label: r.label, value: r[metric] ?? null })) },
                ] })
                const accent = 'linear-gradient(90deg,#0891b2,#22d3ee)'
                return (
                  <StatStrip hub={hub} items={[
                    { key: 'ecomp', label: 'Companies', value: integer(em.companies), accent, hint: 'Distinct companies reached, counted by resolved identity.', drill: seg('companies', 'Companies') },
                    { key: 'econt', label: 'Contacts Reached', value: integer(em.contactsReached), accent, hint: 'Contacts sent at least one marketing email in the window.', drill: seg('contacts', 'Contacts') },
                    { key: 'edel', label: 'Delivered', value: integer(em.delivered), accent, hint: 'CONTACTS with at least one delivered email — contact-grain, not a message count. Bounces excluded.', drill: seg('delivered', 'Delivered') },
                    { key: 'eopen', label: 'Opened', value: integer(em.opened), accent, hint: 'Contacts who opened at least one email.', drill: seg('opened', 'Opened') },
                    { key: 'erep', label: 'Replied', value: integer(em.replied), accent, hint: 'Contacts who replied at least once.', drill: seg('replied', 'Replied') },
                  ]} />
                )
              })()}
            </Section>

            <Section title="ABM · LinkedIn" icon={<LinkedInIcon sx={{ fontSize: 18 }} />} color="#1d4ed8" sources={['HubSpot']}
              subtitle={`Active outreach workflows and what they produced. Values come from the LinkedIn / ABM dashboard${la?.windowLabel ? ` · ${la.windowLabel}` : ''}.`}>
              {!la ? <Clean text="Couldn't load the LinkedIn / ABM summary right now." /> : (
                <StatStrip hub={hub} items={[
                  { key: 'law', label: 'Active Workflows', value: integer(la.activeWorkflows), accent: 'linear-gradient(90deg,#1d4ed8,#60a5fa)',
                    hint: 'Workflows whose status is Active (running). Click to list them.',
                    drill: { tabs: [{ name: 'Active workflows', valueHeader: 'Connections', extraHeader: 'Accepted',
                      rows: (la.activeWorkflowRows || []).map((r) => ({ label: r.label, value: r.connectionsSent, sub: [r.leader, r.category].filter(Boolean).join(' · '), extra: integer(r.connectionsAccepted) })) }] } },
                  { key: 'lcs', label: 'Connections Sent', value: integer(la.activeConnectionsSent), accent: 'linear-gradient(90deg,#1d4ed8,#60a5fa)',
                    hint: 'Connections sent WITHIN the active workflows — same live basis as the count beside it.',
                    drill: { tabs: [
                      { name: 'By workflow', valueHeader: 'Connections', extraHeader: 'Accepted',
                        rows: (la.activeWorkflowRows || []).map((r) => ({ label: r.label, value: r.connectionsSent, sub: r.leader, extra: integer(r.connectionsAccepted) })) },
                      { name: 'By category', valueHeader: 'Connections', extraHeader: 'Replies',
                        note: 'Category totals cover ALL workflows in the window, not only the active ones.',
                        rows: (la.byCategory || []).map((r) => ({ label: r.label, value: r.connectionsSent, extra: integer(r.repliesReceived) })) },
                    ] } },
                  { key: 'lconv', label: 'Conversations Logged', value: integer(la.conversationsLogged), accent: 'linear-gradient(90deg,#1d4ed8,#60a5fa)',
                    hint: 'Replies received across the window’s workflows.',
                    drill: { tabs: [
                      { name: 'By sentiment', valueHeader: 'Conversations', rows: (la.sentiment || []).map((r) => ({ label: r.label, value: r.value })) },
                      { name: 'Conversations', valueHeader: '#', note: 'The reply itself, with contact, company and a HubSpot link — same fields the LinkedIn / ABM dashboard shows. Capped at 200 rows.',
                        rows: (la.conversations || []).map((r, i) => convRow(r, i, hub)) },
                    ] } },
                  { key: 'lpos', label: 'Positive', value: integer(la.positiveConversations), accent: 'linear-gradient(90deg,#059669,#34d399)',
                    hint: 'Conversations whose logged sentiment is Positive.',
                    drill: { tabs: [{ name: 'Positive conversations', valueHeader: '#',
                      note: 'Same row shape as the full conversation list — message, contact, company and HubSpot link.',
                      rows: (la.conversations || []).filter((r) => String(r.sentiment ?? '').toLowerCase().startsWith('positive')).map((r, i) => convRow(r, i, hub)) }] } },
                ]} />
              )}
            </Section>
          </SectionGroup>

          <SectionGroup title="HubSpot" icon={<FactCheckIcon sx={{ fontSize: 16 }} />} color="#0891b2" subtitle="What the CRM itself says — hygiene gaps and how the live book is classified.">
            <Section title="HubSpot Gaps" icon={<FactCheckIcon sx={{ fontSize: 18 }} />} color="#0891b2" sources={['HubSpot']}
              subtitle={hg ? `Open CRM-hygiene gaps · ${integer(hg.totalOpenDeals)} open deals · live as of ${shortDate(hg.asOf)} · click any number` : 'Open CRM-hygiene gaps'}>
              {!hg ? <Clean text="Couldn't load the HubSpot Gaps right now." /> : <HubspotGapsBody hg={hg} hub={hub} />}
            </Section>

            <Section title="Category of Brands" subtitle="Live active brands by HubSpot Industry → sub-category → brands (click to expand)"
              icon={<ViewInArIcon sx={{ fontSize: 18 }} />} color="#0d9488" count={(data.categoryOfBrands?.rows || []).length} sources={['HubSpot', 'ProShip']}>
              <DataTable
                rows={catRows}
                rowKey={(r) => r.key}
                filter={(r, q) => r.imatch.includes(q)}
                filterPlaceholder="Filter industry / sub-category…"
                emptyText="No live brands in this window."
                onRowClick={(r) => {
                  if (r.level === 'industry') { setOpenInd(openInd === r.key ? null : r.key); setOpenSub(null) }
                  else if (r.level === 'sub') setOpenSub(openSub === r.key ? null : r.key)
                }}
                columns={[
                  { key: 'name', header: 'Industry / Sub-category / Brand',
                    cell: (r) => {
                      if (r.level === 'industry') return <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 1, fontWeight: 600 }}><ChevronRightIcon sx={{ fontSize: 15, transition: 'transform .15s', transform: openInd === r.key ? 'rotate(90deg)' : 'none' }} />{r.ind.industry}</Box>
                      if (r.level === 'sub') return <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 1, pl: 3, color: '#475569' }}><ChevronRightIcon sx={{ fontSize: 14, transition: 'transform .15s', transform: openSub === r.key ? 'rotate(90deg)' : 'none' }} />{r.sub.subcategory}</Box>
                      return <Box component="span" sx={{ pl: 6 }}>{r.brand.brandName} <Box component="span" sx={{ fontSize: 11, color: GX_INK_400 }}>· {r.brand.poc}</Box></Box>
                    } },
                  { key: 'brands', header: 'Brands', title: 'Number of live brands (display-hidden brands are counted but not named).', cell: (r) => (r.level === 'industry' ? integer(r.ind.brandCount) : r.level === 'sub' ? integer(r.sub.brandCount) : '—') },
                  { key: 'ship', header: 'Shipments', title: 'ProShip shipments in the selected window.', cell: (r) => integer(r.level === 'industry' ? r.ind.shipments : r.level === 'sub' ? r.sub.shipments : r.brand.shipments) },
                  { key: 'rev', header: 'Revenue', title: 'ProShip revenue (₹) in the selected window.', cell: (r) => currency(r.level === 'industry' ? r.ind.revenue : r.level === 'sub' ? r.sub.revenue : r.brand.revenue) },
                ]}
              />
            </Section>
          </SectionGroup>
        </>
      )}
    </Box>
  )
}
